import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Plus, Pencil, Trash2, MapPin, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function StadiumsPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const [stadiums, setStadiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: '', city: '' });
  const [editForm, setEditForm] = useState({ id: '', name: '', city: '' });
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchStadiums = useCallback(async () => {
    try {
      const res = await api.get('/api/stadiums');
      setStadiums(res.data);
    } catch { toast.error('Failed to load stadiums'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStadiums(); }, [fetchStadiums]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      await api.post('/api/stadiums', form);
      toast.success('Stadium added');
      setOpen(false);
      setForm({ name: '', city: '' });
      fetchStadiums();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleUpdate = async () => {
    if (!editForm.name.trim()) return toast.error('Name is required');
    try {
      await api.put(`/api/stadiums/${editForm.id}`, { name: editForm.name, city: editForm.city });
      toast.success('Stadium updated');
      setEditOpen(false);
      fetchStadiums();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this stadium?')) return;
    try {
      await api.delete(`/api/stadiums/${id}`);
      toast.success('Stadium deleted');
      fetchStadiums();
    } catch { toast.error('Failed to delete'); }
  };

  const handleImport = async () => {
    if (!importFile) return toast.error('Select a file');
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/api/stadiums/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.added} stadiums (${res.data.skipped} skipped)`);
      fetchStadiums();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  const filtered = stadiums.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('stadiums') || 'Stadiums'}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('stadiums_desc') || 'Manage match venues'}</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button data-testid="import-stadiums-btn" variant="outline" size="sm" onClick={() => { setImportOpen(true); setImportFile(null); setImportResult(null); }}>
                <Upload className="h-4 w-4 mr-1" />{t('import') || 'Import'}
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              {hasPerm('create_stadiums') && (
              <DialogTrigger asChild>
                <Button data-testid="add-stadium-btn" size="sm"><Plus className="h-4 w-4 mr-1" />{t('add') || 'Add'}</Button>
              </DialogTrigger>
              )}
              <DialogContent className="bg-white dark:bg-gray-900 max-w-md">
                <DialogHeader><DialogTitle>{t('add_stadium') || 'Add Stadium'}</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>{t('name') || 'Name'}</Label>
                    <Input data-testid="stadium-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Kingdom Arena" />
                  </div>
                  <div>
                    <Label>{t('city') || 'City'}</Label>
                    <Input data-testid="stadium-city-input" value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} placeholder="e.g. Riyadh" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-stadium-btn" onClick={handleCreate}>{t('save') || 'Save'}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="stadium-search"
                className="pl-9"
                placeholder={t('search') || 'Search...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <MapPin className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p>{stadiums.length === 0 ? (t('no_stadiums_yet') || 'No stadiums yet. Add one or sync from matches.') : (t('no_results') || 'No results')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>{t('name') || 'Name'}</TableHead>
                    <TableHead>{t('city') || 'City'}</TableHead>
                    <TableHead className="w-24 text-right">{t('actions') || 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s, i) => (
                    <TableRow key={s.id} data-testid={`stadium-row-${s.id}`}>
                      <TableCell className="text-gray-400 text-sm">{i + 1}</TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-gray-500">{s.city}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {hasPerm('edit_stadiums') && (
                          <Button
                            data-testid={`edit-stadium-${s.id}`}
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => { setEditForm({ id: s.id, name: s.name, city: s.city }); setEditOpen(true); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          )}
                          {hasPerm('delete_stadiums') && (
                          <Button
                            data-testid={`delete-stadium-${s.id}`}
                            variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(s.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-white dark:bg-gray-900 max-w-md">
            <DialogHeader><DialogTitle>{t('edit_stadium') || 'Edit Stadium'}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>{t('name') || 'Name'}</Label>
                <Input data-testid="edit-stadium-name" value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} />
              </div>
              <div>
                <Label>{t('city') || 'City'}</Label>
                <Input data-testid="edit-stadium-city" value={editForm.city} onChange={e => setEditForm(p => ({...p, city: e.target.value}))} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="update-stadium-btn" onClick={handleUpdate}>{t('save') || 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="bg-white dark:bg-gray-900 max-w-lg">
            <DialogHeader><DialogTitle>{t('import_stadiums') || 'Import Stadiums'}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-500">Upload a CSV or Excel file with columns: <strong>name</strong> (required), <strong>city</strong> (optional). Duplicates will be skipped.</p>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                <input
                  data-testid="import-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
                {importFile && <p className="text-xs text-gray-400 mt-2">{importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</p>}
              </div>
              {importResult && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">{importResult.added} added</span>
                    <span className="text-amber-600 font-medium">{importResult.skipped} skipped</span>
                    <span className="text-gray-400">{importResult.total} total rows</span>
                  </div>
                  {importResult.results?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto text-xs space-y-0.5">
                      {importResult.results.map((r, i) => (
                        <div key={i} className={`flex justify-between py-1 px-2 rounded ${r.status === 'added' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          <span>{r.name}</span>
                          <span className="font-medium">{r.status}{r.reason ? ` (${r.reason})` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}>{t('close') || 'Close'}</Button>
              {!importResult && (
                <Button data-testid="import-submit-btn" onClick={handleImport} disabled={!importFile || importing}>
                  {importing ? 'Importing...' : (t('import') || 'Import')}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
